import React, { useState } from 'react'
import { Box, Button, Divider, Menu, MenuItem, Stack, Typography } from '@mui/material'
import { useCustomTranslation } from '../../Hooks/useCustomTranslation'
import { SortOption } from './types'
import { CurrentSort } from '../Table/types'
import { makeStyles } from 'tss-react/mui'
import { ArrowDropDown, ArrowDropUp, KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material'

export interface SortMenuProps<T> {
  sortableColumns: SortOption<T>[]
  value: CurrentSort<T>
  onChange: (sort: CurrentSort<T>) => void
  dataQa?: string
}

const useStyles = makeStyles()(theme => ({
  openMenuButton: {
    backgroundColor: theme.palette.filter.inactiveBackground,
    borderColor: theme.palette.filter.inactiveBorder,
    color: theme.palette.filter.inactiveText,
    height: theme.shape.filter.height,
    borderRadius: theme.spacing(theme.shape.borderRadius),
    display: 'inline-flex',
    alignItems: 'center',
    borderStyle: 'solid',
    borderWidth: 1,
  },
  openMenuButtonActive: {
    backgroundColor: theme.palette.filter.activeBackground,
    borderColor: theme.palette.filter.activeBorder,
    color: theme.palette.filter.activeText,
    '&:hover': {
      borderColor: theme.palette.filter.activeBorder,
      backgroundColor: theme.palette.filter.activeBackground,
    },
  },
  menuItem: {
    minWidth: 150,
    height: theme.shape.filter.menuItemHeight,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  divider: {
    borderColor: theme.palette.grey.grey05,
  },
}))

export const SortMenu = <T,>({ sortableColumns, value, onChange, dataQa }: SortMenuProps<T>): React.ReactElement => {
  const [currentSortName, setCurrentSortName] = useState(value.name)
  const [currentSortDirection, setCurrentSortDirection] = useState(value.direction)

  const { t } = useCustomTranslation()
  const { classes, cx } = useStyles()

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const isMenuOpen = Boolean(anchorEl)

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
    event.stopPropagation()
    setAnchorEl(event.currentTarget)
  }

  const handleClose = (): void => {
    setAnchorEl(null)
  }

  const label = sortableColumns.findIndex(column => column.value === value.name)

  return (
    <>
      <Button
        onClick={handleClick}
        data-qa={dataQa ?? 'mobile-sort-button'}
        aria-label={'Mobile sort button'}
        className={cx(classes.openMenuButton, { [classes.openMenuButtonActive]: isMenuOpen })}
      >
        <Typography variant="caption" alignSelf="center">
          {sortableColumns[label].label} {value.direction === 'asc' ? t('common.ascending') : t('common.descending')}
        </Typography>
        {value.direction === 'asc' ? <KeyboardArrowUp style={{ height: 20, width: 20 }} /> : <KeyboardArrowDown style={{ height: 20, width: 20 }} />}
      </Button>
      <Menu anchorEl={anchorEl} open={isMenuOpen} onClose={handleClose} data-qa="sort-open-menu">
        <Box>
          {sortableColumns.map(column => {
            const isSelected = column.value === currentSortName

            return (
              <MenuItem
                key={`${String(column.value)}-${column.label}`}
                onClick={() => setCurrentSortName(column.value)}
                data-qa={`sort-option-${String(column.value)}`}
                className={classes.menuItem}
                sx={theme => ({
                  backgroundColor: isSelected ? theme.palette.primary.light : undefined,
                  color: isSelected ? theme.palette.primary.dark : undefined,
                })}
              >
                <Typography>{column.label}</Typography>
              </MenuItem>
            )
          })}
        </Box>

        <Divider sx={theme => ({ borderColor: theme.palette.grey.grey05 })} />

        <Box>
          <MenuItem
            onClick={() => setCurrentSortDirection('asc')}
            data-qa="sort-option-ascending"
            className={classes.menuItem}
            sx={theme => ({
              backgroundColor: currentSortDirection === 'asc' ? theme.palette.primary.light : undefined,
              color: currentSortDirection === 'asc' ? theme.palette.primary.dark : undefined,
            })}
          >
            <Typography>{t('common.ascending')}</Typography>
            <ArrowDropUp />
          </MenuItem>
          <MenuItem
            onClick={() => setCurrentSortDirection('desc')}
            data-qa="sort-option-descending"
            className={classes.menuItem}
            sx={theme => ({
              backgroundColor: currentSortDirection === 'desc' ? theme.palette.primary.light : undefined,
              color: currentSortDirection === 'desc' ? theme.palette.primary.dark : undefined,
            })}
          >
            <Typography>{t('common.descending')}</Typography>
            <ArrowDropDown />
          </MenuItem>
        </Box>

        <Divider className={classes.divider} />

        <Stack flexDirection={'row'} justifyContent={'flex-end'} p={1}>
          <Button
            size="small"
            variant="contained"
            onClick={() => {
              onChange({ name: currentSortName, direction: currentSortDirection })
              handleClose()
            }}
            data-qa={'sort-apply-button'}
          >
            <Typography variant="caption" alignSelf="center">
              {t('common.apply')}
            </Typography>
          </Button>
        </Stack>
      </Menu>
    </>
  )
}
